import { LogManager } from '@src/utility/Logger';

import { sameOriginalIndices } from '../../models/karaoke/CaptionLineBreak';
import { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import { rebuildPhraseClips } from '../../services/syntheticCaptionBreaks';
import { AbstractCommand } from '../AbstractCommand';

import type { CaptionLineBreak } from '../../models/karaoke/CaptionLineBreak';
import type { VideoEditor } from '../../VideoEditor';

const logger = LogManager.getLogger('karaoke:syntheticBreak');

/**
 * PLAT-9623: drop a synthetic line break by id and merge the adjacent
 * sub-clips back into one (or fewer, depending on remaining breaks for
 * the same phrase).
 */
export class RemoveSyntheticCaptionBreakCommand extends AbstractCommand {
  private breakId: string;
  private prevClips: KaraokeClip[] | null = null;
  private prevBreaks: CaptionLineBreak[] | null = null;

  constructor(editor: VideoEditor, breakId: string) {
    super(editor);
    this.breakId = breakId;
  }

  getName(): string {
    return 'RemoveSyntheticCaptionBreakCommand';
  }

  getDescription(): string {
    return `Remove synthetic caption break ${this.breakId}`;
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) {
      logger.debug('execute: no karaoke track');
      return false;
    }

    const target = track.syntheticBreaks.find(b => b.id === this.breakId);
    if (!target) {
      logger.debug('execute: break not found', { breakId: this.breakId });
      return false;
    }

    const prevClips = [...track.karaokeClips];
    const prevBreaks = [...track.syntheticBreaks];

    const remainingBreaksForPhrase = prevBreaks.filter(
      b =>
        b.id !== this.breakId &&
        sameOriginalIndices(
          b.phraseOriginalIndices,
          target.phraseOriginalIndices,
        ),
    );

    const rebuilt = rebuildPhraseClips({
      prevClips,
      phraseOriginalIndices: target.phraseOriginalIndices,
      breaksForPhrase: remainingBreaksForPhrase,
    });
    if (!rebuilt) {
      logger.debug('execute: phrase rebuild refused', {
        breakId: this.breakId,
      });
      return false;
    }

    // Commit undo state only after the rebuild succeeded.
    this.prevClips = prevClips;
    this.prevBreaks = prevBreaks;

    const minIdx = rebuilt.phraseClipIndices[0];
    const maxIdx =
      rebuilt.phraseClipIndices[rebuilt.phraseClipIndices.length - 1];
    track.karaokeClips = [
      ...prevClips.slice(0, minIdx),
      ...rebuilt.replacementClips,
      ...prevClips.slice(maxIdx + 1),
    ];
    track.syntheticBreaks = prevBreaks.filter(b => b.id !== this.breakId);

    this.editor.markDirty('synthetic_caption_break_removed');
    return true;
  }

  undo(): boolean {
    if (!this.prevClips || !this.prevBreaks) return false;
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;
    track.karaokeClips = this.prevClips;
    track.syntheticBreaks = this.prevBreaks;
    this.editor.markDirty('synthetic_caption_break_removed_undo');
    return true;
  }
}
